import { ImageOff, LoaderCircle } from 'lucide-react'
import { useEffect, useState, type ImgHTMLAttributes } from 'react'
import { ApiError, apiBlob, isTransientStatus } from '../api/client'

/** Причина, по которой фото не показано: отказ в доступе и отсутствие файла — не то же самое, что нет сети. */
export function imageFailureText(error: unknown): string {
  if (error instanceof ApiError && error.status === 401) return 'Войдите снова, чтобы открыть фото'
  if (error instanceof ApiError && error.status === 403) return 'Нет доступа к фото'
  if (error instanceof ApiError && error.status === 404) return 'Фото не найдено'
  if (error instanceof ApiError && error.status >= 500) return 'Фото временно недоступно'
  return navigator.onLine ? 'Фото не загрузилось' : 'Подключитесь к сети, чтобы загрузить фото'
}

function canRetryImage(error: unknown): boolean {
  return !(error instanceof ApiError) || isTransientStatus(error.status)
}

const objectUrls = new Map<string, Promise<string>>()

function loadImage(apiPath: string): Promise<string> {
  let promise = objectUrls.get(apiPath)
  if (!promise) {
    promise = apiBlob(apiPath).then((blob) => URL.createObjectURL(blob))
    objectUrls.set(apiPath, promise)
    promise.catch(() => objectUrls.delete(apiPath))
  }
  return promise
}

/** Загружает защищённое изображение (/api/v1/files/{id}) с токеном и показывает его через object URL. */
export function AuthImage({ src, alt, fallback, ...props }: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & { src: string; alt: string; fallback?: string }) {
  const [state, setState] = useState<{ src: string; url: string | null; failed: boolean; reason: string; retryable: boolean }>({ src, url: null, failed: false, reason: '', retryable: false })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let active = true
    let retryableFailure = false
    const apiPath = src.replace(/^\/api\/v1/, '')
    function load() {
      loadImage(apiPath)
        .then((url) => { if (active) setState({ src, url, failed: false, reason: '', retryable: false }) })
        .catch((error: unknown) => {
          if (!active) return
          retryableFailure = canRetryImage(error)
          setState({ src, url: null, failed: true, reason: imageFailureText(error), retryable: retryableFailure })
        })
    }
    function recover() {
      if (!active || !retryableFailure) return
      retryableFailure = false
      setState({ src, url: null, failed: false, reason: '', retryable: false })
      load()
    }
    function onVisible() {
      if (document.visibilityState === 'visible') recover()
    }
    load()
    window.addEventListener('online', recover)
    window.addEventListener('focus', recover)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      active = false
      window.removeEventListener('online', recover)
      window.removeEventListener('focus', recover)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [src, attempt])

  function retry() {
    setState({ src, url: null, failed: false, reason: '', retryable: false })
    setAttempt((value) => value + 1)
  }

  const current = state.src === src ? state : { src, url: null, failed: false, reason: '', retryable: false }
  if (current.failed && current.retryable) return <button type="button" className="auth-image auth-image--failed auth-image--retry" aria-label={`Повторить загрузку фото: ${alt}`} onClick={retry}><ImageOff size={22} aria-hidden="true" /><span>{fallback || current.reason}</span><span className="auth-image__retry">Повторить</span></button>
  if (current.failed) return <div className="auth-image auth-image--failed" role="img" aria-label={alt}><ImageOff size={22} /><span>{fallback || current.reason || 'Фото не загрузилось'}</span></div>
  if (!current.url) return <div className="auth-image auth-image--loading" role="img" aria-label={alt}><LoaderCircle className="spin" size={20} /></div>
  return <img src={current.url} alt={alt} {...props} />
}
